import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MoldOrder, MoldOrderStatus as MoldOrderStatusType } from '../../types/mold';
import { isFinalStep } from '../MoldDetail/utils';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import MoldOrderStatus from '../shared/MoldOrderStatus';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import { moldOrderService } from '../../services/api';
import { Layers, AlertCircle, Clock, CheckSquare, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { NavigateFunction } from 'react-router-dom';
import { ProductPartsCell } from '../MoldList/TableCell';
import MoldOrderCardTypeLabel from '../shared/MoldOrderCardTypeLabel';
import MoldOrderDueDateLabel from '../shared/MoldOrderDueDateLabel';

const MAX_MOLD_ORDERS = 4;

interface SecondGanttChartProps {
    moldOrders: MoldOrder[];
}

interface DayColumn {
    date: Date;
    formattedDate: string;
    moldOrders: MoldOrder[];
    isPadding?: boolean; // Add this field
}

interface MoldOrderCardProps {
    moldOrder: MoldOrder;
    index: number;
    navigate: NavigateFunction;
    daysToShow: number;  // Add this prop
}

// Status card for statistics section
interface StatusStat {
    status: MoldOrderStatusType | string;
    count: number;
    color: string;
    icon: React.ReactNode;
}

// New interface for assignee statistics
interface AssigneeStat {
    userId: number;
    name: string;
    count: number;
}

const MoldOrderCard: React.FC<MoldOrderCardProps> = ({ moldOrder, index, navigate, daysToShow }) => {
    const shouldShowShortVersion = daysToShow === 30;

    return (
        <Draggable draggableId={moldOrder.id.toString()} index={index}>
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`bg-white hover:brightness-90 rounded p-1.5 mb-1 cursor-pointer 
                        w-full h-full shadow-lg hover:shadow-xl
                        ${daysToShow === 30 ? 'text-[10px] md:text-xs' : 'text-xs md:text-sm'}`}
                    onClick={() => navigate(`${ROUTES.MOLD_ORDERS}/${moldOrder.id}?source=kanban`)}
                >
                    <div className={`flex flex-col gap-1 ${daysToShow === 3 ? 'px-1' : ''}`}>
                        <div className="pt-1 flex justify-between items-center">
                            <span className="font-medium truncate">{moldOrder.mold.sbiId}</span>
                            <MoldOrderStatus moldOrder={moldOrder} isShortVersion={true} />
                        </div>
                        {!shouldShowShortVersion && <div className="text-xs truncate">
                            รหัสใบงาน #{moldOrder.id}
                        </div>}
                        {!shouldShowShortVersion && <div className="text-sm truncate">
                            <ProductPartsCell parts={moldOrder.mold.productParts} showPreview={true} />
                        </div>}
                        {!shouldShowShortVersion && (
                            <div className="flex justify-between pt-1">
                                {daysToShow === 3 && <span className="text-xs p-2 rounded-full bg-blue-100 text-blue-800">
                                    {moldOrder?.mold?.moldNumber}
                                </span>}
                                <MoldOrderCardTypeLabel moldOrder={moldOrder} />
                            </div>
                        )}
                        {!shouldShowShortVersion && (
                            <div className={`${daysToShow === 3 ? 'pt-1 pl-1' : ''}`}>
                                <MoldOrderDueDateLabel moldOrder={moldOrder} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Draggable>
    );
};

// Helper to compute shift from plannedWorkingDate
const getShiftFromDate = (plannedWorkingDate?: string) => {
    if (!plannedWorkingDate) return 'morning'; // Default to morning if missing
    const date = new Date(plannedWorkingDate);
    const hour = date.getHours();
    return hour < 12 ? 'morning' : 'night';
};

// Helper to set time for shift
const setDateToShift = (date: Date, shift: 'morning' | 'night') => {
    const newDate = new Date(date);
    if (shift === 'morning') {
        newDate.setHours(8, 0, 0, 0); // 08:00
    } else {
        newDate.setHours(13, 0, 0, 0); // 13:00
    }
    return newDate;
};

const SecondGanttChart: React.FC<SecondGanttChartProps> = ({ moldOrders }) => {
    const [daysToShow, setDaysToShow] = useState<number>(7);
    const [startDate, setStartDate] = useState<Date>(new Date()); // Move this line above
    const [dayColumns, setDayColumns] = useState<DayColumn[]>([]);
    const [unscheduledOrders, setUnscheduledOrders] = useState<MoldOrder[]>([]);
    const [localMoldOrders, setLocalMoldOrders] = useState<MoldOrder[]>([]);
    const [showUnscheduledSection, setShowUnscheduledSection] = useState<boolean>(false);
    const [expandedColumns, setExpandedColumns] = useState<Set<number>>(new Set());
    const navigate = useNavigate();

    const handlePrevDays = () => {
        const newStartDate = new Date(startDate);
        newStartDate.setDate(startDate.getDate() - daysToShow);
        setStartDate(newStartDate);
    };

    const handleNextDays = () => {
        const newStartDate = new Date(startDate); // Clone the current startDate
        newStartDate.setDate(newStartDate.getDate() + daysToShow); // Add daysToShow to the cloned date
        setStartDate(newStartDate);
    };

    const handleSetToday = () => {
        setStartDate(new Date());
    };

    // Calculate statistics for mold orders by status
    const statusStats = useMemo((): StatusStat[] => {
        const stats: Record<string, number> = {};

        // Count orders by status
        moldOrders.forEach(order => {
            if (stats[order.status]) {
                stats[order.status]++;
            } else {
                stats[order.status] = 1;
            }
        });

        // Define display properties for each status
        const displayStats: StatusStat[] = [
            {
                status: MoldOrderStatusType.DRAFT,
                count: stats[MoldOrderStatusType.DRAFT] || 0,
                color: 'bg-gray-100 border-gray-300 text-gray-700',
                icon: <Clock className="w-5 h-5 text-gray-500" />
            },
            {
                status: MoldOrderStatusType.IN_PROGRESS,
                count: stats[MoldOrderStatusType.IN_PROGRESS] || 0,
                color: 'bg-blue-100 border-blue-300 text-blue-700',
                icon: <Layers className="w-5 h-5 text-blue-500" />
            },
            {
                status: MoldOrderStatusType.DONE,
                count: stats[MoldOrderStatusType.DONE] || 0,
                color: 'bg-green-100 border-green-300 text-green-700',
                icon: <CheckSquare className="w-5 h-5 text-green-500" />
            },
            {
                status: MoldOrderStatusType.END_REJECTED,
                count: stats[MoldOrderStatusType.END_REJECTED] || 0,
                color: 'bg-red-100 border-red-300 text-red-700',
                icon: <AlertCircle className="w-5 h-5 text-red-500" />
            }
        ];

        return displayStats;
    }, [moldOrders]);

    // Calculate statistics for mold orders by assignee
    const assigneeStats = useMemo((): AssigneeStat[] => {
        const stats: Record<number, AssigneeStat> = {};

        // Group active orders by current assignee
        moldOrders
            .filter(order => !isFinalStep(order.status))
            .forEach(order => {
                if (order.assignedTo && order.assignedTo.id) {
                    const userId = order.assignedTo.id;
                    if (stats[userId]) {
                        stats[userId].count++;
                    } else {
                        stats[userId] = {
                            userId,
                            name: order.assignedTo.fullname || order.assignedTo.username || 'Unknown',
                            count: 1
                        };
                    }
                }
            });

        // Convert to array and sort by count (descending)
        return Object.values(stats)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5); // Show top 5 assignees
    }, [moldOrders]);

    // Calculate the total count of active orders (not completed)
    const activeOrdersCount = useMemo(() => {
        return moldOrders.filter(order => !isFinalStep(order.status)).length;
    }, [moldOrders]);

    // Process and organize data when moldOrders or daysToShow change
    const processData = useCallback((): void => {
        // Filter out completed orders and orders without plannedWorkingDate for calendar
        const activeOrders = localMoldOrders.filter(order =>
            !isFinalStep(order.status) && !!order.plannedWorkingDate
        );

        // Filter out unscheduled orders and exclude completed/finished ones
        const ordersWithoutDate = localMoldOrders.filter(order =>
            !order.plannedWorkingDate &&
            !isFinalStep(order.status)
        );

        const columns = generateDayColumns(daysToShow, activeOrders, startDate);
        setDayColumns(columns);
        setUnscheduledOrders(ordersWithoutDate);
    }, [daysToShow, localMoldOrders, startDate]); // Added startDate here

    // Initialize and update localMoldOrders when moldOrders prop changes
    useEffect(() => {
        setLocalMoldOrders(moldOrders);
    }, [moldOrders]);

    // Re-process data whenever localMoldOrders or daysToShow changes
    useEffect(() => {
        processData();
    }, [processData, localMoldOrders, daysToShow, startDate]); // Added startDate here

    const generateDayColumns = (numDays: number, orders: MoldOrder[], originalStartDate: Date): DayColumn[] => {
        const columns: DayColumn[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Use the originalStartDate directly
        let startDate = new Date(originalStartDate);
        startDate.setHours(0, 0, 0, 0);

        if (numDays === 30) {
            // For monthly view, start from the first day of the month of the originalStartDate
            startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

            // Add padding days from the previous month
            const firstDayOfMonth = startDate.getDay();
            const paddingDays = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Adjust for Monday start

            for (let i = paddingDays - 1; i >= 0; i--) {
                const paddingDate = new Date(startDate);
                paddingDate.setDate(paddingDate.getDate() - (i + 1));

                columns.push({
                    date: paddingDate,
                    formattedDate: paddingDate.toLocaleDateString('th-TH', {
                        month: 'numeric',
                        day: 'numeric',
                    }),
                    moldOrders: [],
                    isPadding: true
                });
            }

            // Calculate days in the current month
            const daysInMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
            numDays = daysInMonth;
        } else if (numDays === 7) {
            // For weekly view, adjust startDate to the Monday of the week containing originalStartDate
            const currentDayOfWeek = startDate.getDay();
            const daysToGoBack = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1; // Adjust for Monday start
            startDate.setDate(startDate.getDate() - daysToGoBack);
        }

        // Add regular days
        for (let i = 0; i < numDays; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);

            const formattedDate = date.toLocaleDateString('th-TH', {
                month: 'numeric',
                day: 'numeric',
            });

            const dayOrders = orders.filter(order => {
                const plannedDate = order.plannedWorkingDate ? new Date(order.plannedWorkingDate) : null;
                if (!plannedDate) return false;
                plannedDate.setHours(0, 0, 0, 0);
                return plannedDate.getTime() === date.getTime();
            });

            columns.push({
                date,
                formattedDate,
                moldOrders: dayOrders
            });
        }

        // For monthly view, add padding days for the next month to complete the grid
        if (numDays === 30) {
            const lastDate = columns[columns.length - 1].date;
            const lastDayOfMonth = lastDate.getDay();
            const paddingDays = lastDayOfMonth === 0 ? 0 : 7 - lastDayOfMonth;

            for (let i = 1; i <= paddingDays; i++) {
                const paddingDate = new Date(lastDate);
                paddingDate.setDate(lastDate.getDate() + i);

                columns.push({
                    date: paddingDate,
                    formattedDate: paddingDate.toLocaleDateString('th-TH', {
                        month: 'numeric',
                        day: 'numeric',
                    }),
                    moldOrders: [],
                    isPadding: true
                });
            }
        }

        return columns;
    };

    // For 3/7 day view, split each day into morning/night shifts
    const getShiftedDayColumns = useMemo(() => {
        if (daysToShow !== 3 && daysToShow !== 7) return null;
        // For each day, split orders into morning/night and sort by plannedWorkingDate
        return dayColumns.map((col) => {
            const morningOrders = col.moldOrders
                .filter(order => getShiftFromDate(order.plannedWorkingDate) === 'morning')
                .sort((a, b) => new Date(a.plannedWorkingDate).getTime() - new Date(b.plannedWorkingDate).getTime());
            const nightOrders = col.moldOrders
                .filter(order => getShiftFromDate(order.plannedWorkingDate) === 'night')
                .sort((a, b) => new Date(a.plannedWorkingDate).getTime() - new Date(b.plannedWorkingDate).getTime());
            return {
                ...col,
                morningOrders,
                nightOrders,
            };
        });
    }, [dayColumns, daysToShow]);

    // Check if a date is in the past
    const isPastDay = (date: Date): boolean => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date.getTime() < today.getTime();
    };

    // Update getColumnHeaderClass to use minimal color scheme
    const getColumnHeaderClass = (date: Date): string => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isToday = date.getTime() === today.getTime();
        const isPast = isPastDay(date);

        // Minimal color scheme: gray for past, blue for today, default for future
        const baseClass = 'border-b';

        if (isPast && !isToday) {
            return `${baseClass} bg-gray-100 text-gray-400`;
        }
        if (isToday) {
            return `${baseClass} bg-blue-100 text-blue-700 font-bold`;
        }
        return `${baseClass} bg-white text-gray-700`;
    };

    // Update getColumnBackground to use minimal color scheme
    const getColumnBackground = (date: Date): string => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isToday = date.getTime() === today.getTime();
        const isPast = isPastDay(date);

        if (isPast && !isToday) {
            return 'bg-gray-50 border border-gray-200';
        }
        if (isToday) {
            return 'bg-blue-50 border-2 border-blue-400 shadow-md';
        }
        return 'bg-white border';
    };

    const getMidpointDate = (before: string | undefined, after: string | undefined, fallback: Date) => {
        if (before && after) {
            const beforeTime = new Date(before).getTime();
            const afterTime = new Date(after).getTime();
            return new Date((beforeTime + afterTime) / 2);
        }
        if (before && !after) {
            // If adding at the end, next 30 mins from last card
            return new Date(new Date(before).getTime() + 30 * 60 * 1000);
        }
        if (!before && after) {
            // If adding at the start, 30 mins before first card
            return new Date(new Date(after).getTime() - 30 * 60 * 1000);
        }
        return fallback;
    };

    const onDragEnd = async (result: DropResult): Promise<void> => {
        const { source, destination } = result;
        if (!destination) return;

        const orderId = parseInt(result.draggableId);
        let moldOrder: MoldOrder | undefined;
        const isShiftedView = daysToShow === 3 || daysToShow === 7;

        if (isShiftedView) {
            const [srcDayIdx, srcShift] = source.droppableId.split('-');
            const [destDayIdx, destShift] = destination.droppableId.split('-');
            const srcIdx = parseInt(srcDayIdx);
            const destIdx = parseInt(destDayIdx);

            if (source.droppableId === 'unscheduled') {
                moldOrder = unscheduledOrders.find(order => order.id === orderId);
            } else if (!isNaN(srcIdx) && getShiftedDayColumns && getShiftedDayColumns[srcIdx]) {
                const orders = srcShift === 'morning'
                    ? getShiftedDayColumns[srcIdx].morningOrders
                    : getShiftedDayColumns[srcIdx].nightOrders;
                moldOrder = orders.find(order => order.id === orderId);
            }

            if (!moldOrder) return;

            if (destination.droppableId !== 'unscheduled') {
                if (isNaN(destIdx) || !getShiftedDayColumns || !getShiftedDayColumns[destIdx]) return;
                const destOrders = destShift === 'morning'
                    ? getShiftedDayColumns[destIdx].morningOrders
                    : getShiftedDayColumns[destIdx].nightOrders;
                const fallbackDate = setDateToShift(getShiftedDayColumns[destIdx].date, destShift as 'morning' | 'night');

                // Find before/after plannedWorkingDate
                const beforeOrder = destOrders[destination.index - 1];
                const afterOrder = destOrders[destination.index];
                const beforeDate = beforeOrder?.plannedWorkingDate;
                const afterDate = afterOrder?.plannedWorkingDate;

                let newDate = getMidpointDate(beforeDate, afterDate, fallbackDate);

                // If newDate is not in the same day, fallback to shift start
                const dayStart = setDateToShift(getShiftedDayColumns[destIdx].date, destShift as 'morning' | 'night');
                if (
                    newDate.toDateString() !== dayStart.toDateString() ||
                    (destShift === 'morning' && newDate.getHours() < 6) ||
                    (destShift === 'night' && newDate.getHours() < 12)
                ) {
                    newDate = fallbackDate;
                }

                try {
                    await moldOrderService.updateMoldOrder(orderId, {
                        plannedWorkingDate: newDate.toISOString()
                    });
                    setLocalMoldOrders(prev =>
                        prev.map(order =>
                            order.id === orderId
                                ? { ...order, plannedWorkingDate: newDate.toISOString() }
                                : order
                        )
                    );
                    toast.success('Updated planned working date and shift');
                } catch (error) {
                    toast.error('Failed to update planned working date');
                }
            } else {
                // Move to unscheduled
                try {
                    await moldOrderService.updateMoldOrder(orderId, {
                        plannedWorkingDate: null
                    });
                    setLocalMoldOrders(prev =>
                        prev.map(order =>
                            order.id === orderId
                                ? { ...order, plannedWorkingDate: "" }
                                : order
                        )
                    );
                    toast.success('Removed planned working date');
                } catch (error) {
                    toast.error('Failed to update planned working date');
                }
            }
            return;
        }

        // ...existing code for 30-day view...
        // Check if dragged from unscheduled section
        const isFromUnscheduled = source.droppableId === 'unscheduled';
        if (isFromUnscheduled) {
            moldOrder = unscheduledOrders.find(order => order.id === orderId);
        } else {
            const sourceColumnIndex = parseInt(source.droppableId);
            if (isNaN(sourceColumnIndex) || !dayColumns[sourceColumnIndex]) return;

            moldOrder = dayColumns[sourceColumnIndex].moldOrders.find(
                order => order.id === orderId
            );
        }

        if (!moldOrder) return;

        if (destination.droppableId !== 'unscheduled') {
            const destColumnIndex = parseInt(destination.droppableId);
            if (isNaN(destColumnIndex) || !dayColumns[destColumnIndex]) return;

            const destOrders = dayColumns[destColumnIndex].moldOrders;
            const fallbackDate = dayColumns[destColumnIndex].date;

            // Find before/after plannedWorkingDate
            const beforeOrder = destOrders[destination.index - 1];
            const afterOrder = destOrders[destination.index];
            const beforeDate = beforeOrder?.plannedWorkingDate;
            const afterDate = afterOrder?.plannedWorkingDate;

            let newDate = getMidpointDate(beforeDate, afterDate, fallbackDate);

            // If newDate is not in the same day, fallback to column date
            if (newDate.toDateString() !== fallbackDate.toDateString()) {
                newDate = fallbackDate;
            }

            try {
                await moldOrderService.updateMoldOrder(orderId, {
                    plannedWorkingDate: newDate.toISOString()
                });

                // ...existing code for updating local state...
                // ...existing code...
                const updatedColumns = [...dayColumns];

                if (!isFromUnscheduled) {
                    const sourceColumnIndex = parseInt(source.droppableId);
                    if (!isNaN(sourceColumnIndex) && updatedColumns[sourceColumnIndex]) {
                        const sourceColumn = updatedColumns[sourceColumnIndex];
                        sourceColumn.moldOrders = sourceColumn.moldOrders.filter(
                            order => order.id !== orderId
                        );
                    }
                } else {
                    setUnscheduledOrders(prev => prev.filter(order => order.id !== orderId));
                }

                const destColumn = updatedColumns[destColumnIndex];
                if (moldOrder) {
                    const updatedOrder: MoldOrder = {
                        ...moldOrder,
                        plannedWorkingDate: newDate.toISOString()
                    };
                    destColumn.moldOrders.splice(destination.index, 0, updatedOrder);

                    setLocalMoldOrders(prev =>
                        prev.map(order =>
                            order.id === orderId
                                ? { ...order, plannedWorkingDate: newDate.toISOString() }
                                : order
                        )
                    );
                }

                setDayColumns(updatedColumns);
                toast.success('Updated planned working date');
            } catch (error) {
                console.error('Failed to update planned working date', error);
                toast.error('Failed to update planned working date');
            }
        }
        // If destination is unscheduled section, remove plannedWorkingDate
        else if (destination.droppableId === 'unscheduled') {
            try {
                // Update the order to remove plannedWorkingDate using moldOrderService
                await moldOrderService.updateMoldOrder(orderId, {
                    plannedWorkingDate: null
                });

                // Remove from source column
                const sourceColumnIndex = parseInt(source.droppableId);
                if (!isNaN(sourceColumnIndex) && dayColumns[sourceColumnIndex]) {
                    const sourceColumn = dayColumns[sourceColumnIndex];
                    sourceColumn.moldOrders = sourceColumn.moldOrders.filter(
                        order => order.id !== orderId
                    );
                }

                // Add to unscheduled list
                const updatedOrder: MoldOrder = { ...moldOrder, plannedWorkingDate: "" };
                const newUnscheduled = [...unscheduledOrders];
                newUnscheduled.splice(destination.index, 0, updatedOrder);
                setUnscheduledOrders(newUnscheduled);

                setDayColumns([...dayColumns]);

                // Update the localMoldOrders array to persist changes when view changes
                setLocalMoldOrders(prev =>
                    prev.map(order =>
                        order.id === orderId
                            ? { ...order, plannedWorkingDate: "" }
                            : order
                    )
                );

                toast.success('Removed planned working date');
            } catch (error) {
                console.error('Failed to remove planned working date', error);
                toast.error('Failed to update planned working date');
            }
        }
    };

    // Helper to get day name in Thai
    const getThaiDayName = (date: Date): string => {
        const dayNames: string[] = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];
        return dayNames[date.getDay()];
    };

    // Helper for view button styles
    const getViewButtonClass = (days: number): string => {
        return `px-4 py-2 transition-all duration-200 ${daysToShow === days
            ? 'bg-blue-500 text-white font-medium'
            : 'bg-white hover:bg-gray-100'
            } border`;
    };

    const toggleExpandColumn = (columnIndex: number) => {
        setExpandedColumns((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(columnIndex)) {
                newSet.delete(columnIndex);
            } else {
                newSet.add(columnIndex);
            }
            return newSet;
        });
    };

    return (
        <div className="w-full lg:px-4 py-1">
            {/* Statistics Section - Status */}
            {/* <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">สรุปสถานะใบงาน</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {statusStats.map(stat => (
                        <div
                            key={stat.status}
                            className={`border rounded-lg p-3 ${stat.color} flex items-center justify-between`}
                        >
                            <div className="flex items-center gap-2">
                                {stat.icon}
                                <span className="font-medium">
                                    {stat.status === MoldOrderStatusType.IN_PROGRESS ? 'กำลังดำเนินการ' :
                                        stat.status === MoldOrderStatusType.DRAFT ? 'ร่าง' :
                                            stat.status === MoldOrderStatusType.DONE ? 'เสร็จสิ้น' :
                                                stat.status === MoldOrderStatusType.END_REJECTED ? 'ยกเลิก' : stat.status}
                                </span>
                            </div>
                            <span className="text-xl font-bold">
                                {stat.count}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="flex justify-end mt-2">
                    <span className="text-sm text-gray-600">
                        ใบงานที่กำลังดำเนินการอยู่: <span className="font-semibold">{activeOrdersCount}</span>
                    </span>
                </div>
            </div> */}

            {/* New Statistics Section - Assignees */}
            {/* {assigneeStats.length > 0 && (
                <div className="mb-6">
                    <h3 className="text-lg font-medium mb-2 flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        ภาระงานตามผู้รับผิดชอบ
                    </h3>
                    <div className="bg-white border rounded-lg p-4 shadow-sm">
                        <div className="grid grid-cols-1 gap-3">
                            {assigneeStats.map((stat) => (
                                <div
                                    key={stat.userId}
                                    className="flex items-center justify-between border-b pb-2 last:border-0 last:pb-0"
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="bg-blue-100 text-blue-800 p-1.5 rounded-full">
                                            <User className="w-4 h-4" />
                                        </div>
                                        <span className="font-medium">{stat.name}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="flex items-center bg-gray-100 px-3 py-1 rounded-full">
                                            <span className="text-sm font-medium text-gray-700">{stat.count} ใบงาน</span>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {assigneeStats.length === 0 && (
                                <div className="text-center py-3 text-gray-500">
                                    ไม่มีข้อมูลการมอบหมายงาน
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )} */}

            <DragDropContext onDragEnd={onDragEnd}>
                {/* Unscheduled tasks toggle button */}
                {unscheduledOrders.length > 0 && (
                    <div className="mb-2">
                        <button
                            onClick={() => setShowUnscheduledSection(!showUnscheduledSection)}
                            className="flex items-center gap-2 text-sm font-medium bg-white border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 transition-colors"
                        >
                            <span className="flex items-center bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                {unscheduledOrders.length} ใบงานยังไม่มีกำหนดการ
                            </span>
                            {showUnscheduledSection ?
                                <ChevronUp className="h-4 w-4" /> :
                                <ChevronDown className="h-4 w-4" />
                            }
                        </button>
                    </div>
                )}

                {/* Unscheduled section - collapsible */}
                {showUnscheduledSection && unscheduledOrders.length > 0 && (
                    <div className="mb-4 transition-all duration-300">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-sm overflow-hidden">
                            <div className="p-2 border-b border-amber-200 bg-amber-100 flex items-center justify-between">
                                <h3 className="text-sm font-medium text-amber-800">ใบงานที่ยังไม่ได้กำหนดวันทำงาน</h3>
                                <span className="text-xs text-amber-700 bg-amber-200 px-2 py-0.5 rounded-full">{unscheduledOrders.length} รายการ</span>
                            </div>
                            <Droppable droppableId="unscheduled" direction="horizontal">
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className="p-2 flex flex-wrap gap-2 max-h-[200px] overflow-y-auto"
                                    >
                                        {unscheduledOrders.map((order, index) => (
                                            <div key={order.id} className="w-[150px]">
                                                <MoldOrderCard
                                                    moldOrder={order}
                                                    index={index}
                                                    navigate={navigate}
                                                    daysToShow={daysToShow}
                                                />
                                            </div>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                            <div className="p-2 border-t border-amber-200 bg-amber-100 text-xs text-amber-700 italic text-center">
                                ลากและวางใบงานไปยังวันที่ต้องการในปฏิทิน
                            </div>
                        </div>
                    </div>
                )}

                {/* Navigation buttons */}
                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={handlePrevDays}
                        className="flex items-center justify-start gap-0 lg:gap-2 px-1 lg:px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-xs lg:text-sm shadow-sm transition-all duration-200"
                    >
                        <ChevronLeft size={16} />
                        <span>ก่อนหน้า</span>
                    </button>
                    <div className='flex flex-row items-center gap-2'>
                        <h2 className="text-lg md:text-xl font-semibold">งานที่ต้องดำเนินการในแต่ละวัน</h2>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center rounded-md overflow-hidden shadow-sm w-full md:w-auto">
                                <button
                                    onClick={() => setDaysToShow(3)}
                                    className={`${getViewButtonClass(3)} rounded-l flex items-center gap-1 text-xs flex-1 md:flex-none justify-center`}
                                >
                                    <span>3 วัน</span>
                                </button>
                                <button
                                    onClick={() => setDaysToShow(7)}
                                    className={`${getViewButtonClass(7)} border-x-0 text-xs flex-1 md:flex-none justify-center`}
                                >
                                    7 วัน
                                </button>
                                <button
                                    onClick={() => setDaysToShow(30)}
                                    className={`${getViewButtonClass(30)} rounded-r flex items-center gap-1 text-xs flex-1 md:flex-none justify-center`}
                                >
                                    30 วัน
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row items-center gap-1">
                        <button
                            onClick={handleSetToday}
                            className="px-2 lg:px-4 py-2 bg-blue-500 text-white rounded text-xs lg:text-sm font-medium shadow-sm hover:bg-blue-600 transition-all duration-200"
                        >
                            วันนี้
                        </button>
                        <button
                            onClick={handleNextDays}
                            className="flex items-center justify-start gap-0 lg:gap-2 px-2 lg:px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-xs lg:text-sm shadow-sm transition-all duration-200"
                        >
                            <span>ถัดไป</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>

                {/* Calendar section */}
                <div className="overflow-x-auto -mx-2 md:mx-0">
                    <div className="min-w-[640px] md:min-w-0"> {/* Minimum width for mobile scroll */}
                        {/* Month header */}
                        {daysToShow === 30 && (
                            <div className="flex items-center justify-between bg-white border-x border-t rounded-t-lg p-2 md:p-3">
                                <h4 className="text-base md:text-lg font-medium">
                                    {startDate.toLocaleDateString('th-TH', {
                                        month: 'long',
                                        year: 'numeric'
                                    })}
                                </h4>
                            </div>
                        )}

                        {/* Calendar grid */}
                        <div className={`grid ${daysToShow === 3 ? 'grid-cols-3' : 'grid-cols-7'} gap-0.5 w-full ${daysToShow === 30 ? 'border-x border-b rounded-b-lg overflow-hidden' : ''
                            }`}>
                            {/* Calendar headers */}
                            {daysToShow === 30 && (
                                <>
                                    {['จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.'].map((day) => (
                                        <div key={day} className="text-center py-1 md:py-2 bg-gray-50 font-medium text-[10px] md:text-sm border-b">
                                            {day}
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Calendar columns */}
                            {daysToShow === 3 || daysToShow === 7 ? (
                                getShiftedDayColumns && getShiftedDayColumns.map((column, columnIndex) => {
                                    const isToday = new Date(column.date).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
                                    const isPast = isPastDay(new Date(column.date));
                                    return (
                                        <div
                                            key={columnIndex}
                                            className={`
                                            transition-all duration-300 
                                            px-0.5 md:px-1 mb-4
                                            ${getColumnBackground(column.date)} 
                                            ${isToday ? 'z-10' : ''}
                                            overflow-hidden
                                        `}
                                            style={{
                                                minWidth: daysToShow === 3 ? `calc(100% / 3)` : (daysToShow === 7 ? `calc(100% / 7)` : "120px"),
                                            }}
                                        >
                                            {/* Column header */}
                                            <div
                                                className={`
                                                ${getColumnHeaderClass(column.date)}
                                                p-2 md:p-3
                                                font-medium text-center truncate
                                            `}
                                            >
                                                <div className="font-bold">{getThaiDayName(column.date)}</div>
                                                <div>{column.formattedDate}</div>
                                            </div>
                                            {/* Morning shift */}
                                            <div
                                                className="rounded-lg border-2 border-blue-400 bg-blue-50 shadow-sm mb-2"
                                                style={{ minHeight: 80 }}
                                            >
                                                <div className="text-xs font-semibold flex items-center gap-1 py-1 px-2 border-b border-blue-200 bg-blue-100 text-blue-800 rounded-t-lg">
                                                    ☀️ กะเช้า
                                                </div>
                                                <Droppable
                                                    droppableId={`${columnIndex}-morning`}
                                                    isDropDisabled={isPast && !isToday}
                                                >
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.droppableProps}
                                                            className="p-1 md:p-2 min-h-[60px]"
                                                        >
                                                            {column.morningOrders.map((order, idx) => (
                                                                <MoldOrderCard
                                                                    key={order.id}
                                                                    moldOrder={order}
                                                                    index={idx}
                                                                    navigate={navigate}
                                                                    daysToShow={daysToShow}
                                                                />
                                                            ))}
                                                            {provided.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                            {/* Night shift */}
                                            <div
                                                className="rounded-lg border-2 border-violet-400 bg-violet-50 shadow-sm"
                                                style={{ minHeight: 80 }}
                                            >
                                                <div className="text-xs font-semibold flex items-center gap-1 py-1 px-2 border-b border-violet-200 bg-violet-100 text-violet-800 rounded-t-lg">
                                                    🌙 กะดึก
                                                </div>
                                                <Droppable
                                                    droppableId={`${columnIndex}-night`}
                                                    isDropDisabled={isPast && !isToday}
                                                >
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.droppableProps}
                                                            className="p-1 md:p-2 min-h-[60px]"
                                                        >
                                                            {column.nightOrders.map((order, idx) => (
                                                                <MoldOrderCard
                                                                    key={order.id}
                                                                    moldOrder={order}
                                                                    index={idx}
                                                                    navigate={navigate}
                                                                    daysToShow={daysToShow}
                                                                />
                                                            ))}
                                                            {provided.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                // ...existing code for 30-day view...
                                dayColumns.map((column, columnIndex) => {
                                    const isToday = new Date(column.date).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
                                    const isPast = isPastDay(new Date(column.date));
                                    const isCurrentMonth = column.date.getMonth() === new Date().getMonth();

                                    const sortedOrders = [...column.moldOrders].sort(
                                        (a, b) => new Date(a.plannedWorkingDate).getTime() - new Date(b.plannedWorkingDate).getTime()
                                    );
                                    const isExpanded = expandedColumns.has(columnIndex);
                                    const visibleOrders = isExpanded ? sortedOrders : sortedOrders.slice(0, MAX_MOLD_ORDERS);
                                    const remainingOrders = column.moldOrders.length - visibleOrders.length;

                                    return (
                                        <div
                                            key={columnIndex}
                                            className={`
                                            transition-all duration-300 
                                            ${daysToShow === 30 ? 'border-b border-r' : 'px-0.5 md:px-1 mb-4'} 
                                            ${getColumnBackground(column.date)} 
                                            ${isToday ? 'z-10' : ''}
                                            ${column.isPadding ? 'opacity-50' : ''}
                                            overflow-hidden
                                        `}
                                            style={{
                                                minWidth: daysToShow === 30 ? "0" : daysToShow === 3 ? `calc(100% / 3)` : "120px",
                                                minHeight: daysToShow === 30 ? "80px" : "auto",
                                            }}
                                        >
                                            {/* Column header */}
                                            <div
                                                className={`
                                                ${getColumnHeaderClass(column.date)}
                                                ${daysToShow === 30 ? 'p-0.5 md:p-1 text-[10px] md:text-sm' : 'p-2 md:p-3'} 
                                                font-medium text-center truncate
                                            `}
                                            >
                                                {daysToShow === 30 ? (
                                                    <span className={`${isCurrentMonth ? 'font-medium' : 'text-gray-400'}`}>
                                                        {column.date.getDate()}
                                                    </span>
                                                ) : (
                                                    <>
                                                        <div className="font-bold">{getThaiDayName(column.date)}</div>
                                                        <div>{column.formattedDate}</div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Column content */}
                                            <Droppable
                                                droppableId={columnIndex.toString()}
                                                isDropDisabled={isPast && !isToday || column.isPadding}
                                            >
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.droppableProps}
                                                        className={`
                                                        ${daysToShow === 30 ? 'p-0.5 min-h-[60px] md:min-h-[80px]' : 'p-1 md:p-2 min-h-[120px] md:min-h-[150px]'}
                                                        ${(isPast && !isToday) || column.isPadding ? 'opacity-50' : ''}
                                                    `}
                                                    >
                                                        <div className="space-y-1">
                                                            {visibleOrders.map((order, index) => (
                                                                <MoldOrderCard
                                                                    key={order.id}
                                                                    moldOrder={order}
                                                                    index={index}
                                                                    navigate={navigate}
                                                                    daysToShow={daysToShow}
                                                                />
                                                            ))}
                                                            {remainingOrders > 0 && (
                                                                <button
                                                                    onClick={() => toggleExpandColumn(columnIndex)}
                                                                    className="w-full bg-gray-200 text-gray-700 text-xs rounded p-1 hover:bg-gray-300"
                                                                >
                                                                    {isExpanded
                                                                        ? "แสดงน้อยลง"
                                                                        : `+${remainingOrders} ใบงานเพิ่มเติม`}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </DragDropContext>
        </div>
    );
};

export default SecondGanttChart;
